import type { JSX } from "react";
import { Icon, UnreadCount } from "../ui";

export interface NotificationBellProps {
  /** Number of chats needing a reply — the badge is hidden at 0. */
  readonly count: number;
  readonly onClick: () => void;
}

/**
 * Global needs-reply bell in the command bar, attached right of the
 * "Search or jump to…" field and visible from ANY module. Opens the
 * NeedsReplyPopover quick-switch. Round-4 polish: h-9 matches the
 * search field's 36px height, the badge stays inside the button
 * bounds, and focus follows the IconButton pattern — outline only on
 * keyboard :focus-visible, never a ring on mouse click.
 */
// @tested-by: tst_fe_bell_001, tst_fe_bell_002, tst_fe_bell_003, tst_fe_bell_004
// @invariant: badge hidden at 0; count shown above 0; click delegates
export function NotificationBell({ count, onClick }: NotificationBellProps): JSX.Element {
  return (
    <button
      type="button"
      data-no-window-drag
      onClick={onClick}
      // No mouse focus → no focus ring can ever paint on click (some
      // browser :focus-visible heuristics keep keyboard modality after
      // typing in search). Tab focus still works and shows the ring.
      onMouseDown={(e) => { e.preventDefault(); }}
      title="Needs reply"
      aria-label="Needs reply"
      className="relative flex items-center justify-center w-9 h-9 shrink-0 rounded-lg
        hover:text-content hover:bg-surface-hover/50
        transition-colors duration-100 border-none bg-transparent cursor-pointer
        text-content-tertiary
        outline-none focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-accent/70 focus-visible:outline-offset-2"
    >
      <Icon name="bell" size={16} />
      {count > 0 && (
        <UnreadCount
          count={count}
          className="absolute top-0.5 right-0.5 w-[15px] h-[15px] text-[9px]"
        />
      )}
    </button>
  );
}
